// ABOUTME: Custom hook that encapsulates data fetching and state management for TradeList.
// ABOUTME: Handles loading tradespeople from various sources, filtering, removal/undo, and animation tracking.

import { useState, useEffect, useCallback } from 'react';
import { Tradesperson } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';
import { deleteUserTradesperson, addUserTradesperson, getPostcodeTradespeople, getTradesPeopleByPostcodeWithCounts, getTradesPeopleByUsername, getTradesPeopleByUsernameWithCounts, getPopularTradespeople, getRecentTradespeople } from '@/lib/supabase/trades';
import { addBadgeInfo } from '@/lib/badgeUtils';
import { debug } from '@/lib/utils';
import { formatPostcodeDisplay } from '@/lib/postcodeUtils';
import { toast } from 'sonner';
import { api } from '@/lib/supabase/client';
import { getTradeType } from '@/lib/tradeUtils';

// Define available list modes
export type TradeListMode = 'personal' | 'public' | 'results' | 'search';

// Define data sources
export type DataSource = 'user' | 'username' | 'postcode' | 'popular' | 'provided';

export interface UseTradeListDataOptions {
  dataSource: DataSource;
  mode: TradeListMode;
  postcodeOrUsername?: string;
  userId?: string;
  providedTradespeople?: Tradesperson[];
  categoryFilter: string | null;
  refreshTrigger?: number;
  onDataLoaded?: (trades: Tradesperson[]) => void;
  onTradespersonSaved?: (trade: Tradesperson) => void;
  syncWithUrl?: boolean;
  isFilterNavigation?: boolean;
}

export interface UseTradeListDataResult {
  tradespeople: Tradesperson[];
  filteredTradespeople: Tradesperson[];
  isLoading: boolean;
  error: string | null;
  username: string | undefined;
  locationName: string | null;
  relationIds: Record<string, string>;
  tradeNotes: Record<string, string>;
  removedTradeCache: Record<string, Tradesperson>;
  categories: string[];
  activeFilter: string | null;
  newItemIds: Set<string>;
  handleRemoveTrade: (tradeId: string) => Promise<void>;
  handleUndoRemove: (tradeId: string) => Promise<void>;
  handleSaveTrade: (trade: Tradesperson) => void;
  loadTradespeople: () => Promise<void>;
  setActiveFilter: (filter: string | null) => void;
  setFilteredTradespeople: (trades: Tradesperson[]) => void;
}

export function useTradeListData({
  dataSource,
  mode,
  postcodeOrUsername,
  userId,
  providedTradespeople,
  categoryFilter,
  refreshTrigger,
  onDataLoaded,
  onTradespersonSaved,
  isFilterNavigation = false,
}: UseTradeListDataOptions): UseTradeListDataResult {
  const { user } = useAuth();

  // Core state
  const [tradespeople, setTradespeople] = useState<Tradesperson[]>(providedTradespeople || []);
  const [filteredTradespeople, setFilteredTradespeople] = useState<Tradesperson[]>(providedTradespeople || []);
  const [isLoading, setIsLoading] = useState(dataSource !== 'provided');
  const [error, setError] = useState<string | null>(null);

  // Metadata state
  const [username, setUsername] = useState<string | undefined>(undefined);
  const [locationName, setLocationName] = useState<string | null>(null);

  // Relationship state
  const [relationIds, setRelationIds] = useState<Record<string, string>>({});
  const [tradeNotes, setTradeNotes] = useState<Record<string, string>>({});
  const [removedTradeCache, setRemovedTradeCache] = useState<Record<string, Tradesperson>>({});

  // Category filtering state
  const [activeFilter, setActiveFilter] = useState<string | null>(categoryFilter);
  const [categories, setCategories] = useState<string[]>([]);

  // Animation tracking
  const [newItemIds, setNewItemIds] = useState<Set<string>>(new Set());
  const [previousItemIds, setPreviousItemIds] = useState<Set<string>>(new Set());
  const [lastRefreshTrigger, setLastRefreshTrigger] = useState<number | undefined>(refreshTrigger);

  // Load username for sharing when in personal mode. The authenticated user
  // already carries username (via /auth/session) — the old code queried Supabase
  // directly, which is dead post-D1-migration.
  useEffect(() => {
    if (!user || mode !== 'personal') return;
    if (user.username) setUsername(user.username);
  }, [user, mode]);

  // Clear new item IDs after animations complete
  useEffect(() => {
    if (newItemIds.size > 0) {
      const timer = setTimeout(() => {
        setNewItemIds(new Set());
      }, 500); // Clear after animations complete

      return () => clearTimeout(timer);
    }
  }, [newItemIds]);

  // Load tradespeople data based on data source
  const loadTradespeople = useCallback(async () => {
    // Skip load if filtering navigation or if we already have provided data
    if (isFilterNavigation || dataSource === 'provided') {
      setIsLoading(false);
      return;
    }

    // Always start with loading state
    setIsLoading(true);
    setError(null);

    try {
      let trades: Tradesperson[] = [];
      let userInfo = null;
      let recentTrades: Tradesperson[] = [];

      // Fetch data based on data source
      switch (dataSource) {
        case 'user': {
          // User's saved tradespeople
          if (!user) {
            setIsLoading(false);
            return;
          }

          // Get recently used tradespeople if we're in personal mode
          if (mode === 'personal') {
            try {
              recentTrades = await getRecentTradespeople(user.id, 3); // Limit to 3 recent trades
            } catch (err) {
              debug.error('Failed to load recent tradespeople:', err);
              // Continue even if this fails
            }
          }

          // Get tradespeople and the relationship IDs and notes. The Worker's
          // GET /user-tradespeople returns each relation as { id, notes,
          // tradesperson } — the old code queried Supabase directly (dead).
          const { user_tradespeople: userTradeRelations } = await api.get<{
            user_tradespeople: Array<{ id: string; notes: string | null; tradesperson: Tradesperson | null }>;
          }>('/user-tradespeople');

          if (userTradeRelations) {
            // Extract tradespeople and build relation ID map, filtering out orphaned relations
            trades = userTradeRelations
              .filter(relation => relation.tradesperson !== null)
              .map(relation => relation.tradesperson) as Tradesperson[];
            const idMap: Record<string, string> = {};
            const notesMap: Record<string, string> = {};

            userTradeRelations.forEach(relation => {
              if (relation.tradesperson?.id) {
                idMap[relation.tradesperson.id] = relation.id;
                notesMap[relation.tradesperson.id] = relation.notes || '';
              }
            });

            setRelationIds(idMap);
            // Store notes in state for passing to cards
            setTradeNotes(notesMap);

            // Log any orphaned relations for debugging
            const orphanedRelations = userTradeRelations.filter(relation => !relation.tradesperson);
            if (orphanedRelations.length > 0) {
              debug.error('Found orphaned user_tradespeople relations:', orphanedRelations);
            }

            // Sort trades alphabetically by org name (tradesperson's business name)
            trades = [...trades].sort((a, b) => {
              // Use organization name for sorting with null safety
              const nameA = a?.org?.toLowerCase() || '';
              const nameB = b?.org?.toLowerCase() || '';
              return nameA.localeCompare(nameB);
            });
          }
          break;
        }

        case 'username': {
          // Tradespeople shared by a specific username
          if (!postcodeOrUsername) {
            throw new Error("Username is required for username data source");
          }

          // Use new function that includes save counts for results mode
          const result = mode === 'results'
            ? await getTradesPeopleByUsernameWithCounts(postcodeOrUsername)
            : await getTradesPeopleByUsername(postcodeOrUsername);

          trades = result.tradespeople;
          userInfo = result.userData;

          // Set username for display
          setUsername(postcodeOrUsername);

          if (!result.userData) {
            throw new Error(`No user found with username "${postcodeOrUsername}"`);
          }

          // Add badge information for results mode
          if (mode === 'results' && trades.length > 0) {
            // Exclude "new" badge for username-based results since we're sorting by popularity
            trades = addBadgeInfo(trades, true);
          }

          // For results mode, sort by popularity (saved count)
          // For other modes, sort alphabetically by organization name
          if (mode === 'results') {
            trades = [...trades].sort((a, b) => {
              const countA = a.save_count || a.recommendation_count || 0;
              const countB = b.save_count || b.recommendation_count || 0;
              return countB - countA; // Sort by descending count
            });
          } else {
            trades = [...trades].sort((a, b) => {
              const nameA = a.org?.toLowerCase() || '';
              const nameB = b.org?.toLowerCase() || '';
              return nameA.localeCompare(nameB);
            });
          }
          break;
        }

        case 'postcode': {
          // Tradespeople in a specific postcode area
          if (!postcodeOrUsername) {
            throw new Error("Postcode is required for postcode data source");
          }

          // Use the new function that includes save counts for results mode
          if (mode === 'results') {
            trades = await getTradesPeopleByPostcodeWithCounts(postcodeOrUsername);
            // Add badge information based on save counts and creation dates
            trades = addBadgeInfo(trades);
          } else {
            trades = await getPostcodeTradespeople(postcodeOrUsername);
          }

          // Set location for display
          setLocationName(formatPostcodeDisplay(postcodeOrUsername));

          // For results mode, sort by popularity (saved count)
          // For other modes, sort alphabetically by organization name
          if (mode === 'results') {
            trades = [...trades].sort((a, b) => {
              const countA = a.save_count || 0;
              const countB = b.save_count || 0;
              return countB - countA; // Sort by descending count
            });
          } else {
            trades = [...trades].sort((a, b) => {
              const nameA = a.org?.toLowerCase() || '';
              const nameB = b.org?.toLowerCase() || '';
              return nameA.localeCompare(nameB);
            });
          }
          break;
        }

        case 'popular': {
          // Popular/featured tradespeople (already sorted by popularity in getPopularTradespeople)
          trades = await getPopularTradespeople();
          // Add badge information for popular tradespeople
          if (mode === 'results') {
            trades = addBadgeInfo(trades);
          }
          break;
        }

        default: {
          // If we reach here with 'provided' source, data should already be set
          if (providedTradespeople) {
            trades = providedTradespeople;

            // For results mode, sort by popularity (saved count)
            // For other modes, sort alphabetically by organization name
            if (mode === 'results') {
              trades = [...trades].sort((a, b) => {
                const countA = a.recommendation_count || 0;
                const countB = b.recommendation_count || 0;
                return countB - countA; // Sort by descending count
              });
            } else {
              trades = [...trades].sort((a, b) => {
                const nameA = a.org?.toLowerCase() || '';
                const nameB = b.org?.toLowerCase() || '';
                return nameA.localeCompare(nameB);
              });
            }
          }
        }
      }

      // Detect new items for animation
      const currentItemIds = new Set(trades.map(trade => trade.id));
      const isRefreshTriggered = refreshTrigger !== lastRefreshTrigger;

      if (isRefreshTriggered) {
        // If refresh was triggered, animate all new items
        const genuinelyNewItems = new Set(
          Array.from(currentItemIds).filter(id => !previousItemIds.has(id))
        );
        setNewItemIds(genuinelyNewItems);
        setLastRefreshTrigger(refreshTrigger);
      } else {
        // If it's just a regular tab switch or filter change, don't animate anything
        setNewItemIds(new Set());
      }

      setPreviousItemIds(currentItemIds);

      // Update state with the fetched data
      setTradespeople(trades);

      // Set filtered tradespeople based on active filter
      if (!activeFilter) {
        setFilteredTradespeople(trades);
      } else {
        const filtered = trades.filter(trade =>
          getTradeType(trade)?.toLowerCase() === activeFilter.toLowerCase()
        );
        setFilteredTradespeople(filtered);
      }

      // Extract categories from trades
      if (trades.length > 0) {
        const uniqueCategories = Array.from(
          new Set(
            trades
              .map(trade => getTradeType(trade))
              .filter(Boolean) as string[]
          )
        );
        setCategories(uniqueCategories);
      }

      // Invoke the callback with the loaded data
      if (onDataLoaded) {
        onDataLoaded(trades);
      }
    } catch (error) {
      debug.error(`Failed to load tradespeople (${dataSource}):`, error);
      setError(error instanceof Error ? error.message : 'Failed to load tradespeople.');
      setTradespeople([]);
      setFilteredTradespeople([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, dataSource, postcodeOrUsername, providedTradespeople, activeFilter, isFilterNavigation, onDataLoaded, mode, refreshTrigger, lastRefreshTrigger, previousItemIds]);

  // Initial data load
  useEffect(() => {
    loadTradespeople();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, postcodeOrUsername, userId]);

  // Reset state when data source changes
  useEffect(() => {
    // Reset state when data source changes (except for filtering)
    if (!isFilterNavigation) {
      setActiveFilter(categoryFilter);
      setError(null);
    }
  }, [dataSource, categoryFilter, isFilterNavigation]);

  // Handle removal of a tradesperson (only in personal mode)
  const handleRemoveTrade = useCallback(async (tradeId: string) => {
    if (!user || mode !== 'personal') return;

    // Find the tradesperson to cache for potential undo
    const tradeToRemove = tradespeople.find(t => t.id === tradeId);
    if (!tradeToRemove) return;

    // Get the relation ID for this tradesperson
    const relationId = relationIds[tradeId];
    if (!relationId) {
      toast.error("Failed to remove tradesperson: relationship not found");
      return;
    }

    try {
      // Cache the tradesperson before removal
      setRemovedTradeCache(prev => ({
        ...prev,
        [tradeId]: tradeToRemove
      }));

      // Delete the relationship
      await deleteUserTradesperson(relationId);

      // Update the UI by removing the tradesperson from the list
      setTradespeople(trades => trades.filter(t => t.id !== tradeId));
    } catch (error) {
      debug.error('Failed to remove tradesperson:', error);
      throw error; // Rethrow for the calling component to handle
    }
  }, [user, mode, tradespeople, relationIds]);

  // Handle undoing a removal (only in personal mode)
  const handleUndoRemove = useCallback(async (tradeId: string) => {
    if (!user || mode !== 'personal') return;

    // Get the cached tradesperson
    const tradeToRestore = removedTradeCache[tradeId];
    if (!tradeToRestore) {
      toast.error("Failed to restore tradesperson: not found in cache");
      return;
    }

    try {
      // Add the tradesperson back to the user's list
      await addUserTradesperson(user.id, tradeId, {
        preferred: false,
        notes: ""
      });

      // Refresh the list to get the new relation ID
      await loadTradespeople();

      // Remove from the cache
      const { [tradeId]: _, ...restCache } = removedTradeCache;
      setRemovedTradeCache(restCache);
    } catch (error) {
      debug.error('Failed to restore tradesperson:', error);
      throw error; // Rethrow for the calling component to handle
    }
  }, [user, mode, removedTradeCache, loadTradespeople]);

  // Handle save action on public cards
  const handleSaveTrade = useCallback((trade: Tradesperson) => {
    // Call the provided callback if available
    if (onTradespersonSaved) {
      onTradespersonSaved(trade);
    }

    // If in personal mode, refresh data to show the saved trade
    if (mode === 'personal') {
      loadTradespeople();
    }
  }, [onTradespersonSaved, mode, loadTradespeople]);

  return {
    tradespeople,
    filteredTradespeople,
    isLoading,
    error,
    username,
    locationName,
    relationIds,
    tradeNotes,
    removedTradeCache,
    categories,
    activeFilter,
    newItemIds,
    handleRemoveTrade,
    handleUndoRemove,
    handleSaveTrade,
    loadTradespeople,
    setActiveFilter,
    setFilteredTradespeople,
  };
}
